import { useEffect, useRef, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { motion } from 'framer-motion';

import './RollPowerLaunch.css';
import Button from '../Button/Button';
import { PreRollSVG, handleHomeClick, playSound } from '../../utils/utils';
import { useOptionalWarpTransition } from '../WarpTransition/WarpTransitionProvider';
import { lockBodyScroll } from '../../utils/bodyScrollLock';

export const ROLL_CHARGE_MS = 3600;
const ELECTRIC_ARCS = Array.from({ length: 8 }, (_, index) => index);
const POWER_PARTICLES = Array.from({ length: 24 }, (_, index) => index);

const RollPowerLaunch = ({ onRoll, to, variant = 'round', children }) => {
  const isTiebreaker = variant === 'tiebreaker';

  const history = useHistory();
  const [chargePhase, setChargePhase] = useState(null);
  const isCharging = chargePhase !== null;
  const warp = useOptionalWarpTransition();
  const chargingRef = useRef(false);
  const timersRef = useRef([]);
  const reduceMotion = !!window.matchMedia?.('(prefers-reduced-motion: reduce)')
    .matches;

  useEffect(() => {
    window.scrollTo(0, 0);
    return () => timersRef.current.forEach(clearTimeout);
  }, []);

  useEffect(() => {
    if (isCharging) return lockBodyScroll();
    return undefined;
  }, [isCharging]);

  const handleRollClick = () => {
    if (chargingRef.current || warp?.isTransitioning) return;
    chargingRef.current = true;
    window.scrollTo(0, 0);
    playSound(isTiebreaker ? '/sounds/futuristic_spinning.mp3' : '/sounds/attack.mp3');
    const showResults = () => {
      onRoll();
      history.push(to);
    };

    if (reduceMotion) {
      showResults();
      return;
    }

    setChargePhase('gather');
    playSound('/sounds/epic-get-ready.mp3');
    timersRef.current = [
      setTimeout(() => setChargePhase('overdrive'), 1300),
      setTimeout(() => {
        setChargePhase('release');
        playSound(
          isTiebreaker ? '/sounds/charged-laser-sound.mp3' : '/sounds/futuristic_spinning.mp3'
        );
      }, 2700),
      setTimeout(showResults, ROLL_CHARGE_MS),
    ];
  };

  return (
    <div
      className={`rollPowerLaunch rollPowerLaunch--${variant}${
        isCharging
          ? ` rollPowerLaunch--charging rollPowerLaunch--${chargePhase}`
          : ''
      }`}
      style={{ '--roll-charge-ms': `${ROLL_CHARGE_MS}ms` }}
    >
      {isCharging && (
        <div className="rollPowerField" aria-hidden="true">
          <div className="rollPowerField__aura" />
          <div className="rollPowerField__beam" />
          <div className="rollPowerField__shockwave" />
          <div className="rollPowerField__flash" />
        </div>
      )}
      <Link to="/" tabIndex={isCharging ? -1 : undefined}>
        <div className="header">
          <Button
            title="home"
            type="smallSquare home"
            handleClick={handleHomeClick}
            disabled={isCharging}
          />
        </div>
      </Link>
      {isTiebreaker && <h1 className="rollLaunchHeading">TIEBREAKER</h1>}
      <motion.div
        className="rollChargeStage"
        initial={reduceMotion ? false : { scaleY: 0, scaleX: 0 }}
        animate={{ scaleY: 1, scaleX: 1, rotate: 720 }}
        transition={{
          ease: 'anticipate',
          duration: isCharging || reduceMotion ? 0 : 2,
        }}
        style={{
          transformOrigin: 'center center',
        }}
      >
        <div className="rollChargeCamera">
          <div className="rollButtonGroup">
            {isTiebreaker && (
              <span className="rollDuelReticle" aria-hidden="true" />
            )}
            <div className="rollChargeEnergy" aria-hidden="true">
              <span className="rollChargeEnergy__halo" />
              <span className="rollChargeEnergy__shockwave" />
              <span className="rollChargeEnergy__plasma" />
              {ELECTRIC_ARCS.map((arc) => (
                <span
                  key={arc}
                  className="rollChargeEnergy__arc"
                  style={{
                    '--arc-angle': `${arc * 45}deg`,
                    '--arc-delay': `${arc * 55}ms`,
                  }}
                />
              ))}
            </div>
            {isCharging && (
              <div className="rollPowerStreams" aria-hidden="true">
                {POWER_PARTICLES.map((particle) => (
                  <span
                    key={particle}
                    className="rollPowerStreams__particle"
                    style={{
                      '--particle-angle': `${particle * 15}deg`,
                      '--particle-delay': `${(particle % 7) * -130}ms`,
                    }}
                  />
                ))}
                <svg className="rollPowerLightning" viewBox="0 0 360 360">
                  {ELECTRIC_ARCS.map((arc) => (
                    <g key={arc} transform={`rotate(${arc * 45} 180 180)`}>
                      <path d="M248 180 L290 165 L278 193 L330 158 L317 183 L396 146 L370 181 L485 155" />
                      <path d="M330 158 L345 124 L370 130 L402 94" />
                    </g>
                  ))}
                </svg>
              </div>
            )}
            <Button
              title="ROLL"
              type="largeSquare roll"
              handleClick={handleRollClick}
              disabled={isCharging || warp?.isTransitioning}
            />
            <PreRollSVG handleClick={handleRollClick} />
          </div>
        </div>
        {isCharging && (
          <div className="rollChargeStatus" role="status" aria-live="polite">
            <span className="rollChargeStatus__eyebrow">
              {isTiebreaker
                ? 'Sudden Death Sequence'
                : 'Roll Sequence Initiated'}
            </span>
            <strong>
              {chargePhase === 'gather'
                ? isTiebreaker
                  ? 'SYNCING RIVALS'
                  : 'HARNESSING ENERGY'
                : chargePhase === 'overdrive'
                ? isTiebreaker
                  ? 'DEADLOCK OVERLOAD'
                  : 'LIMITERS OFFLINE'
                : isTiebreaker
                ? 'BREAK THE TIE'
                : 'TRUE POWER ACHIEVED'}
            </strong>
            <span className="rollChargeStatus__meter" aria-hidden="true">
              <span />
            </span>
          </div>
        )}
      </motion.div>

      {children?.(isCharging)}
    </div>
  );
};

export default RollPowerLaunch;
